import range from 'lodash/range';
import { normalize } from 'normalizr';
import { formOptionsSchema } from 'common/entities/formNode';
import {
    getFormNode,
    getFormNodeByName,
    getAdditionalGuestInfoGroupsButFirst,
    getFirstAdditionalGuestInfoGroup,
    getChildrenIdsRecursivelyFromNodeId,
} from 'common/entities/formNode/selectors';
import { getFormNodeVisibilityLogicRules } from 'common/entities/formLogicRule/selectors';
import {
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP,
    NODE_NAME_ADDITIONAL_GUEST_PREFIX_REGEXP,
} from 'common/entities/formNode/nodeNames';
import { INPUT_TYPE_SELECT } from 'common/entities/formNode/inputTypes';
import { getFormId } from '../selectors';
import {
    createFormNode,
    createActionUpdateFormNode,
    createActionUpdateFormNodeConfigValue,
    createActionRemoveFormNode,
} from './nodeActions';
import { duplicateFormNode } from './index';
import { updateFormLogicEffect, replicateAdditionalFormNodeLogicEffect } from '../epics/formLogicEffects';
import { isAdditionalGuestInfoNode } from '../utils/additionalGuestHelperFunctions';

/**
 * Updates the logic associated with the given addtional guest info group so it shows/hides
 * when appropriate.
 * @param {*} addtionalGuestInfoGroupNodeId
 * @param {*} guestCount
 * @return {Function} - thunk that returns an Object with the action dispatched.
 */
export const updateAdditionalGuestCountInfoGroupVisibilityLogic =
    (addtionalGuestInfoGroupNodeId, guestCount) => (dispatch, getState) => {
        const state = getState();
        const node = getFormNode(state, addtionalGuestInfoGroupNodeId);
        const { formId } = node;
        const logicRules = getFormNodeVisibilityLogicRules(state, formId, addtionalGuestInfoGroupNodeId);
        const logicRule = logicRules[0];

        dispatch(
            updateFormLogicEffect({
                logicRuleId: logicRule.id,
                updates: {
                    operandRight: guestCount,
                },
            }),
        );
    };

/**
 * Updates additional guest numbers in the `name` attribute.
 * @param {string} addtionalGuestInfoGroupNodeId
 * @param {number} guestNumber
 * @return {Function} - thunk that returns an Object[] with the actions dispatched.
 */
export const recursivelyUpdateAdditionalGuestNumberInName =
    (addtionalGuestInfoGroupNodeId, guestNumber) => (dispatch, getState) => {
        const state = getState();

        const allChildrenIds = getChildrenIdsRecursivelyFromNodeId(state, addtionalGuestInfoGroupNodeId);
        const allNodes = [
            getFormNode(state, addtionalGuestInfoGroupNodeId),
            ...allChildrenIds.map((id) => getFormNode(state, id)),
        ];

        const nodesToUpdate = allNodes.filter(isAdditionalGuestInfoNode);

        const actions = nodesToUpdate.map((node) => {
            const newName = node.name.replace(NODE_NAME_ADDITIONAL_GUEST_PREFIX_REGEXP, `$1[${guestNumber}]$3`);
            return createActionUpdateFormNode(node, { name: newName });
        });

        actions.forEach((action) => {
            dispatch(action);
        });
    };

/**
 * @param  {string} id - node id
 * @param  {number} maxGuestCount
 * @param  {string} inputType
 */
export const updateMaxGuestCountNode = (nodeId, maxGuestCount, inputType) => (dispatch, getState) => {
    if (!Number.isFinite(maxGuestCount) || maxGuestCount < 1) {
        throw new Error(`maxGuestCount should be a positive number, but ${maxGuestCount} received`);
    }

    const state = getState();
    const formId = getFormId(state);
    const maxGuestCountNode = getFormNode(state, nodeId);

    // Update guest count node type.
    dispatch(createActionUpdateFormNode(maxGuestCountNode, { inputType }));

    // Update guest count node options.
    const newOptions =
        inputType === INPUT_TYPE_SELECT
            ? range(0, maxGuestCount + 1).map((number) => ({
                  label: `${number}`,
                  value: number,
              }))
            : [];
    const newOptionsNormalized = normalize(newOptions, formOptionsSchema).result;
    dispatch(createActionUpdateFormNodeConfigValue(maxGuestCountNode, 'options', newOptionsNormalized));

    // Clear all additional guest info groups but the first.
    const additionalGuestInfoGroups = getAdditionalGuestInfoGroupsButFirst(state, formId);
    additionalGuestInfoGroups.forEach((node) => dispatch(createActionRemoveFormNode(node.id)));

    // Create new node groups.
    const baseAdditionalGuestInfoGroup = getFirstAdditionalGuestInfoGroup(state, formId);
    range(1, maxGuestCount).forEach((number) => {
        const parentId = baseAdditionalGuestInfoGroup.parent;
        const newGroupId = dispatch(duplicateFormNode(parentId, baseAdditionalGuestInfoGroup.id, number + 1));
        dispatch(recursivelyUpdateAdditionalGuestNumberInName(newGroupId, number));
        dispatch(updateAdditionalGuestCountInfoGroupVisibilityLogic(newGroupId, number));
        if (number === maxGuestCount - 1) {
            dispatch(replicateAdditionalFormNodeLogicEffect({}));
        }
    });
};

/**
 * @param {Object} node
 * @param {number} position
 */
export const createFormNodeInAdditionalGuestInformationGroup = (node, position) => (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);
    const firstGuestInformationGroup = getFormNodeByName(state, formId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP);
    const parentId = firstGuestInformationGroup.id;

    dispatch(createFormNode(node, parentId, position));
};
